/**
 * pages/register.tsx
 *
 * Register screen — nickname + emoji avatar picker + password.
 * Step 1 of the "Start our story" onboarding flow.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRegister } from '../hooks/use-auth'
import { isAuthenticated } from '../store/auth'

const EMOJI_OPTIONS = [
  '🐻',
  '🦊',
  '🐼',
  '🐨',
  '🦁',
  '🐯',
  '🐸',
  '🐺',
  '🦋',
  '🌸',
  '🌻',
  '🌹',
  '🍀',
  '🌙',
  '⭐',
  '🌈',
  '🍓',
  '🍑',
  '🍒',
  '🧁',
  '🍰',
  '☕',
  '🧸',
  '🎀',
]

const StepDots = ({ current, total }: { current: number; total: number }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`rounded-full transition-all duration-300 ${
          i + 1 === current ? 'w-6 h-2 bg-[#D4537E]' : 'w-2 h-2 bg-[#F4C0D1]'
        }`}
      />
    ))}
  </div>
)

export default function RegisterPage() {
  const navigate = useNavigate()
  const { mutate: register, isPending, error } = useRegister()

  useEffect(() => {
    if (isAuthenticated()) navigate('/home', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [nickname, setNickname] = useState('')
  const [avatar, setAvatar] = useState(EMOJI_OPTIONS[0]!)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [nicknameError, setNicknameError] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const validate = () => {
    let ok = true
    if (!nickname.trim() || nickname.trim().length < 1) {
      setNicknameError('Please choose a nickname')
      ok = false
    } else if (nickname.trim().length > 50) {
      setNicknameError('Nickname must be 50 characters or fewer')
      ok = false
    } else {
      setNicknameError('')
    }
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      ok = false
    } else {
      setPasswordError('')
    }
    return ok
  }

  const handleSubmit = () => {
    if (!validate()) return
    register(
      { nickname: nickname.trim(), avatar_emoji: avatar, password },
      {
        onSuccess: () => navigate('/create'),
      },
    )
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] px-5 pt-[env(safe-area-inset-top)] pb-2">
        <div className="flex items-center gap-2 h-12">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-[#D4537E] font-['DM_Sans'] text-[15px] active:opacity-60 transition-opacity duration-100 touch-manipulation -ml-1"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-5 pb-[88px] overflow-y-auto">
        <div className="animate-fade-in-up">
          <StepDots current={1} total={3} />

          {/* Title */}
          <div className="mb-8 [animation-delay:60ms] animate-fade-in-up">
            <h1 className="font-['Playfair_Display'] text-[28px] font-semibold text-[#2C1810] leading-tight mb-2">
              Create your profile
            </h1>
            <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62]">
              Your partner will see this when you share your space.
            </p>
          </div>

          {/* Avatar Picker */}
          <div className="mb-6 [animation-delay:90ms] animate-fade-in-up">
            <p className="font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-3">
              Choose your avatar
            </p>
            {/* Selected preview */}
            <div className="flex justify-center mb-4">
              <div className="w-[72px] h-[72px] rounded-full bg-[#FBEAF0] border-2 border-[#D4537E] flex items-center justify-center text-[36px] shadow-[0_4px_16px_rgba(212,83,126,0.18)]">
                {avatar}
              </div>
            </div>
            {/* Grid */}
            <div className="grid grid-cols-8 gap-2">
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setAvatar(emoji)}
                  className={`
                    w-full aspect-square rounded-[10px] text-[22px] flex items-center justify-center
                    transition-all duration-150 touch-manipulation select-none
                    ${
                      avatar === emoji
                        ? 'bg-[#FBEAF0] border-[1.5px] border-[#D4537E] scale-110'
                        : 'bg-white border border-[#F4C0D1] active:scale-95 active:bg-[#FBEAF0]'
                    }
                  `}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Form Fields */}
          <div className="flex flex-col gap-3 [animation-delay:120ms] animate-fade-in-up">
            {/* Nickname */}
            <div>
              <label className="block font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-1.5">
                Nickname
              </label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="What should they call you?"
                maxLength={50}
                autoComplete="off"
                className={`
                  w-full h-[52px] px-4
                  bg-[#FAF8F4] text-[#2C1810]
                  font-['DM_Sans'] text-[15px]
                  rounded-[12px]
                  border-[1.5px] outline-none
                  placeholder:text-[#B89AA2]
                  transition-all duration-150
                  focus:border-[#D4537E] focus:shadow-[0_0_0_3px_rgba(212,83,126,0.12)]
                  ${nicknameError ? 'border-[#C0392B]' : 'border-[#F4C0D1]'}
                `}
              />
              {nicknameError && (
                <p className="mt-1 font-['DM_Sans'] text-[12px] text-[#C0392B]">{nicknameError}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block font-['DM_Sans'] text-[13px] font-medium text-[#7A5A62] mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  maxLength={100}
                  autoComplete="new-password"
                  className={`
                    w-full h-[52px] px-4 pr-12
                    bg-[#FAF8F4] text-[#2C1810]
                    font-['DM_Sans'] text-[15px]
                    rounded-[12px]
                    border-[1.5px] outline-none
                    placeholder:text-[#B89AA2]
                    transition-all duration-150
                    focus:border-[#D4537E] focus:shadow-[0_0_0_3px_rgba(212,83,126,0.12)]
                    ${passwordError ? 'border-[#C0392B]' : 'border-[#F4C0D1]'}
                  `}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B89AA2] text-[18px] touch-manipulation select-none"
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              {passwordError && (
                <p className="mt-1 font-['DM_Sans'] text-[12px] text-[#C0392B]">{passwordError}</p>
              )}
            </div>

            {/* API error */}
            {error && (
              <p className="font-['DM_Sans'] text-[13px] text-[#C0392B] text-center">
                {error.message}
              </p>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className="
                w-full h-[52px] px-6 mt-2
                bg-[#D4537E] text-white
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px]
                shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                transition-all duration-150 ease-out
                active:bg-[#993556] active:scale-[0.98] active:shadow-none
                disabled:opacity-40 disabled:cursor-not-allowed
                select-none touch-manipulation
              "
            >
              {isPending ? '💕 Creating your profile…' : 'Continue'}
            </button>

            {/* Switch to login */}
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center mt-1">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-[#D4537E] font-medium active:opacity-60 touch-manipulation"
              >
                Sign in →
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
